import { useTranslation } from 'react-i18next'
import { useFilePreviewWithPresigned } from '@/hooks/useFilePreviewWithPresigned'
import { Attachment } from '@/modules/Upload/types'
import DownLoadIcons from '@/icons/DownLoadIcons'
import EyeIcons from '@/icons/EyeIcons'
import { Typogaphy } from 'vnetwork-ui'
import type { DataTableAction } from './types/actions'
import type { DataTableColumn } from './types/column'
import type { DataTableExpandable } from './types/expandable'

/**
 * Build expandable config for attachment rows in DataTableCustomized.
 * Not a hook — call inside a component/hook that already has t() and file preview.
 */
export function buildAttachmentExpandable<T>(options: {
    getAttachments: (row: T) => any[]
    t: (key: string) => string
    openFilePreview: (attachment: Attachment) => void
    downloadFileWithPresigned: (attachment: Attachment) => void
    minToExpand?: number
}): DataTableExpandable<T> {
    const { t, openFilePreview, downloadFileWithPresigned } = options
    const minToExpand = options.minToExpand ?? 2

    const expandedColumns: DataTableColumn<any>[] = [
        {
            key: 'attachment_file_name',
            title: t('attachment_file_name'),
            width: 300,
            cellClassName: 'cursor-pointer',
            render: (_, row: any) => (
                <Typogaphy
                    className="text-xs truncate text-primary hover:underline"
                    onClick={() => openFilePreview(row as Attachment)}
                >
                    {row.attachment_file_name}
                </Typogaphy>
            ),
        },
        {
            key: 'attachment_file_size',
            title: t('attachment_file_size'),
            width: 120,
            render: (value) => (
                <Typogaphy className="text-xs text-gray-500">
                    {value ? formatFileSize(value as number) : '-'}
                </Typogaphy>
            ),
        },
        {
            key: 'attachment_file_type',
            title: t('file_type'),
            width: 100,
            render: (_, row: any) => {
                const ext = row.attachment_file_name
                    ?.split('.')
                    .pop()
                    ?.toUpperCase()
                return (
                    <span className="text-xs text-gray-500">{ext || '-'}</span>
                )
            },
        },
    ]

    const expandedActions: DataTableAction<any>[] = [
        {
            key: 'preview',
            label: t('preview'),
            icon: <EyeIcons className="w-4 h-4" />,
            onClick: (row) => openFilePreview(row as Attachment),
        },
        {
            key: 'download',
            label: t('download'),
            icon: <DownLoadIcons className="w-4 h-4" />,
            onClick: (row) => downloadFileWithPresigned(row as Attachment),
        },
    ]

    return {
        rowExpandable: (row) =>
            (options.getAttachments(row)?.length || 0) >= minToExpand,
        expandedColumns,
        expandedActions,
        expandedRowData: (row) => options.getAttachments(row) || [],
        expandedRowKey: (row) => row.attachment_id,
    }
}

/**
 * Hook wrapper — calls useTranslation + useFilePreviewWithPresigned internally.
 */
export function useAttachmentExpandable<T>(options: {
    getAttachments: (row: T) => any[]
    minToExpand?: number
}): DataTableExpandable<T> {
    const { t } = useTranslation()
    const { openFilePreview, downloadFileWithPresigned } =
        useFilePreviewWithPresigned()

    return buildAttachmentExpandable<T>({
        ...options,
        t,
        openFilePreview,
        downloadFileWithPresigned,
    })
}

function formatFileSize(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}
